import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../Modal';
import Lightbox from '../Lightbox/Lightbox';
import PhotoLibrary from '../PhotoLibrary';
import { contentService } from '../../services/api';
import './PictureStudio.css';

const STARTERS = [
  'A garden gnome in a hazmat suit, horrified by a mouldy path',
  'Andrew and Leanne as superheroes, capes flapping, wands at the ready',
  'A grimy driveway on a dating app, hoping for a glow-up',
  'A sulking mould blob packing its suitcase as our ute pulls up',
  'The pressure washer asleep in a hammock after a 3-day job'
];
const KIND_BADGE = { changed: 'Changed', words: 'Words' };
const STAGE = { brief: 'The art director is working out the picture…', drawing: 'Drawing it…' };

const money = (n) => `US$${Number(n || 0).toFixed(Number(n) < 0.1 ? 3 : 2)}`;

/**
 * The picture studio (backlog #782, GID-Session-025): joke pictures for our posts,
 * made with OpenAI's gpt-image (Get It Posted's Image Studio engine), with Andrew
 * and Leanne drawn from their own photos. Two places use it:
 *   the Content page's Pictures tab   onNewPost(picture), and the cast set-up
 *   a post's "✨ Picture" dialog       postId + onUse(picture) puts it on that post
 */
export default function PictureStudio({ postId = null, status, aiReady = false, onUse, onNewPost, initialScene = '', castEditable = true }) {
  const [cast, setCast] = useState(null);
  const [pics, setPics] = useState(null);
  const [scene, setScene] = useState(initialScene);
  const [who, setWho] = useState([]);
  const [style, setStyle] = useState('cartoon');
  const [shape, setShape] = useState('tall');
  const [quality, setQuality] = useState('medium');
  const [exact, setExact] = useState(false);
  const [ideas, setIdeas] = useState({ loading: false, list: [], error: '' });
  const [making, setMaking] = useState(null); // { started, stage, what }
  const [fresh, setFresh] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [changeText, setChangeText] = useState('');
  const [words, setWords] = useState({ text: '', where: 'bottom', brand: true });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const [castPick, setCastPick] = useState(null); // cast key choosing photos
  const [looks, setLooks] = useState({});
  const [castOpen, setCastOpen] = useState(null); // opens by itself while someone has no photos; then the user's choice
  const [, tick] = useState(0);

  const ready = Boolean(status?.ready);
  const uid = postId ? `-p${postId}` : ''; // the tab and a post's dialog can both be on the page
  const loadPics = useCallback(async () => {
    const r = await contentService.pictures({ limit: 120 });
    if (r.success) setPics(r); else setError(r.error);
  }, []);
  const loadCast = useCallback(async () => {
    const r = await contentService.cast();
    if (!r.success) return setError(r.error);
    setCast(r.cast);
    setLooks(Object.fromEntries(r.cast.map((c) => [c.key, c.look])));
    setCastOpen((o) => (o === null ? r.cast.some((c) => !c.library_ids.length) : o));
  }, []);
  useEffect(() => { loadPics(); loadCast(); }, [loadPics, loadCast]);

  useEffect(() => {
    if (!making) return undefined;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [making]);

  const byId = useMemo(() => new Map((pics?.pictures || []).map((p) => [p.id, p])), [pics]);
  const open = openId ? byId.get(openId) : null;
  const hasPhotos = (key) => (cast || []).some((c) => c.key === key && c.library_ids.length > 0);
  const castName = (key) => (cast || []).find((c) => c.key === key)?.name || key;

  function openPicture(id) {
    setOpenId(id); setChangeText(''); setWords({ text: '', where: 'bottom', brand: true }); setConfirmDelete(false); setError('');
  }

  // ---- ideas ----
  async function askIdeas() {
    setIdeas({ loading: true, list: [], error: '' }); setError('');
    const r = await contentService.pollAi(contentService.pictureIdeas(scene.trim(), postId));
    setIdeas(r.success ? { loading: false, list: r.result.ideas, error: '' } : { loading: false, list: [], error: r.error });
  }
  function takeIdea(i) {
    setScene(i.scene);
    setWho(i.cast.filter(hasPhotos));
    if (status?.styles?.some((s) => s.key === i.style)) setStyle(i.style);
  }

  // ---- make / change ----
  async function runPictureJob(what, start) {
    setMaking({ started: Date.now(), stage: what === 'make' && !exact ? 'brief' : 'drawing', what }); setError('');
    const r = await contentService.pollAi(start, {
      intervalMs: 2500, timeoutMs: 8 * 60 * 1000,
      onProgress: (p) => setMaking((m) => (m ? { ...m, stage: p.stage || m.stage } : m))
    });
    setMaking(null);
    if (!r.success) { setError(r.error); return; }
    const ids = r.result.pictures.map((p) => p.id);
    setFresh(ids);
    await loadPics();
    openPicture(ids[0]);
  }
  const make = () => runPictureJob('make', contentService.makePicture({ scene, cast: who, style, shape, quality, exact, post_id: postId }));
  const change = () => open && changeText.trim() && runPictureJob('change', contentService.changePicture(open.id, changeText.trim(), quality));

  async function addWords() {
    if (!open || !words.text.trim()) return;
    setBusy('words'); setError('');
    const r = await contentService.pictureWords(open.id, { text: words.text.trim(), where: words.where, brand: words.brand });
    setBusy('');
    if (!r.success) return setError(r.error);
    setFresh([r.picture.id]);
    await loadPics();
    openPicture(r.picture.id);
  }

  async function remove() {
    if (!open) return;
    setBusy('delete');
    const r = await contentService.deletePicture(open.id);
    setBusy('');
    if (!r.success) return setError(r.error);
    setOpenId(null);
    await loadPics();
  }

  async function choose(p) {
    setBusy('use'); setError('');
    try {
      if (onUse) await onUse(p); else if (onNewPost) await onNewPost(p);
    } finally { setBusy(''); }
  }

  // ---- cast ----
  async function saveCast(next) {
    const r = await contentService.saveCast(next);
    if (!r.success) { setError(r.error); return false; }
    setCast(r.cast);
    setLooks(Object.fromEntries(r.cast.map((c) => [c.key, c.look])));
    return true;
  }
  const castPhotos = (key, ids) => saveCast([{ key, library_ids: ids }]);
  async function addCastPhotos(ids) {
    const c = cast.find((x) => x.key === castPick);
    const merged = [...new Set([...c.library_ids, ...ids])].slice(0, 4);
    if (await castPhotos(c.key, merged)) setCastPick(null);
  }

  const busyMaking = Boolean(making);
  const elapsed = making ? Math.round((Date.now() - making.started) / 1000) : 0;
  const list = pics?.pictures || [];
  const freshList = fresh.map((id) => byId.get(id)).filter(Boolean);

  return (
    <div className={`picture-studio${postId ? ' in-post' : ''}`}>
      {!ready && (
        <div className="ps-off">Pictures need an OpenAI key on the server (OPENAI_API_KEY). Everything made so far is still here.</div>
      )}

      <div className="ps-grid">
        {/* ---- make ---- */}
        <div className="ps-make">
          <div className="form-group">
            <label htmlFor={`ps-scene${uid}`}>What's the picture?</label>
            <textarea id={`ps-scene${uid}`} rows={4} value={scene} disabled={busyMaking} onChange={(e) => setScene(e.target.value)}
              placeholder="A joke in a sentence or two: who's in it, what's happening. e.g. A garden gnome in a hazmat suit, horrified by a mouldy path" />
            {!scene.trim() && (
              <div className="pe-tweaks ps-starters">
                {STARTERS.map((s) => <button key={s} type="button" className="pe-tweak" onClick={() => setScene(s)}>{s}</button>)}
              </div>
            )}
          </div>

          <div className="ps-ideas-row">
            <button type="button" className="btn btn-outline btn-sm" disabled={!aiReady || ideas.loading || busyMaking} onClick={askIdeas}>
              {ideas.loading ? 'Thinking…' : postId ? '✨ Ideas for this post' : '✨ Give me ideas'}
            </button>
            <span className="text-muted">{scene.trim() ? 'Uses what you typed as the brief.' : postId ? 'Reads the post and comes up with pictures for it.' : 'Or type a theme first ("wet season", "Christmas").'}</span>
          </div>
          {ideas.loading && <p className="pe-ai-wait"><span className="loading-spinner small"></span> Coming up with pictures… usually 20–40 seconds.</p>}
          {ideas.error && <div className="form-error">{ideas.error}</div>}
          {ideas.list.length > 0 && (
            <div className="ps-ideas">
              {ideas.list.map((i, n) => (
                <div key={n} className="ps-idea">
                  <div className="pe-variant-head">
                    <strong>{i.title}</strong>
                    <button type="button" className="btn btn-outline btn-sm" disabled={busyMaking} onClick={() => takeIdea(i)}>Use this</button>
                  </div>
                  <p>{i.scene}</p>
                  <p className="text-muted ps-idea-meta">
                    {[i.cast.length ? i.cast.map(castName).join(' & ') : 'No people', status?.styles?.find((s) => s.key === i.style)?.label].filter(Boolean).join(' · ')}
                    {i.hook ? ` · Opens with: “${i.hook}”` : ''}
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className="form-group">
            <label>Who's in it?</label>
            <div className="pe-tweaks">
              {(cast || []).map((c) => {
                const on = who.includes(c.key);
                const can = c.library_ids.length > 0;
                return (
                  <button key={c.key} type="button" className={`pe-tweak ps-who${on ? ' on' : ''}`} disabled={!can || busyMaking}
                    title={can ? '' : `Add a photo of ${c.name} under Our cast first`}
                    onClick={() => setWho((w) => (on ? w.filter((k) => k !== c.key) : [...w, c.key]))}>
                    {can && <img src={contentService.libraryUrl(c.library_ids[0], 'thumb')} alt="" />}
                    {on ? '✓ ' : ''}{c.name}
                  </button>
                );
              })}
              <span className="text-muted ps-who-none">{who.length ? '' : 'Nobody: the grime, the gear or the house is the star.'}</span>
            </div>
            {cast && cast.some((c) => !c.library_ids.length) && (
              <p className="text-muted ps-hint">{castEditable ? 'Add photos of each of you under Our cast (below) so the AI can draw you.' : 'Add photos of each of you on the Pictures tab (Our cast) so the AI can draw you.'}</p>
            )}
          </div>

          <div className="form-group">
            <label>Style</label>
            <div className="pe-tweaks">
              {(status?.styles || []).map((s) => (
                <button key={s.key} type="button" className={`pe-tweak${style === s.key ? ' on' : ''}`} disabled={busyMaking} onClick={() => setStyle(s.key)}>{s.label}</button>
              ))}
            </div>
          </div>

          <div className="ps-row ps-pair">
            <label>Shape
              <select value={shape} disabled={busyMaking} onChange={(e) => setShape(e.target.value)}>
                {(status?.shapes || []).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
            <label>Quality
              <select value={quality} disabled={busyMaking} onChange={(e) => setQuality(e.target.value)}>
                {(status?.qualities || []).map((q) => <option key={q.key} value={q.key}>{q.label}</option>)}
              </select>
            </label>
          </div>
          <label className="ps-check">
            <input type="checkbox" checked={exact} disabled={busyMaking} onChange={(e) => setExact(e.target.checked)} />
            Send my words as they are (skip the AI art director)
          </label>

          <button type="button" className="btn btn-primary ps-go" disabled={!ready || busyMaking || !scene.trim() || (!exact && !aiReady)} onClick={make}>
            {making?.what === 'make' ? 'Working…' : '🎨 Make 2 pictures'}
          </button>
          <p className="text-muted ps-cost">About {quality === 'high' ? 'US$0.15' : 'US$0.05'} for the pair (with the AI art director's brief). Words go in the post, not the picture (add them after with “Words on it”).</p>
          {making && (
            <p className="pe-ai-wait"><span className="loading-spinner small"></span> {STAGE[making.stage] || 'Working…'} {elapsed} s {making.what === 'change' ? '(changes take 15–40 s)' : '(usually 20–60 s)'}</p>
          )}
          {error && <div className="form-error">{error}</div>}
        </div>

        {/* ---- results ---- */}
        <div className="ps-results">
          {open && (
            <div className="ps-open" data-picture={open.id}>
              <button type="button" className="ps-open-img" onClick={() => setLightbox({ images: [{ src: contentService.pictureUrl(open.id), label: open.title || '' }], index: 0 })}>
                <img src={contentService.pictureUrl(open.id)} alt={open.alt || open.title || 'Picture'} />
              </button>
              <div className="ps-open-side">
                <div className="ps-open-head">
                  <strong>{open.title || 'Picture'}</strong>
                  <button type="button" className="btn-link" onClick={() => setOpenId(null)}>Close</button>
                </div>
                <p className="ps-open-idea">{open.kind === 'made' ? open.idea : open.kind === 'changed' ? `Changed: ${open.idea}` : `Words: “${open.idea}”`}</p>
                <p className="text-muted ps-open-meta">
                  {[status?.styles?.find((s) => s.key === open.style)?.label, open.cast.length ? open.cast.map(castName).join(' & ') : null,
                    open.cost_usd ? money(open.cost_usd) : null, open.created_by_name ? `by ${open.created_by_name}` : null,
                    open.used_on.length ? `on ${open.used_on.length} post${open.used_on.length === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')}
                  {open.parent_id && byId.get(open.parent_id) ? <> · <button type="button" className="btn-link" onClick={() => openPicture(open.parent_id)}>from #{open.parent_id}</button></> : null}
                </p>
                <div className="ps-open-use">
                  {onUse && <button type="button" className="btn btn-primary btn-sm" disabled={!!busy || busyMaking} onClick={() => choose(open)}>{busy === 'use' ? 'Adding…' : 'Use on this post'}</button>}
                  {!onUse && onNewPost && <button type="button" className="btn btn-primary btn-sm" disabled={!!busy || busyMaking} onClick={() => choose(open)}>{busy === 'use' ? 'Starting…' : 'New post with this'}</button>}
                </div>

                <form className="ps-open-tool" onSubmit={(e) => { e.preventDefault(); change(); }}>
                  <label htmlFor={`ps-change${uid}`}>Change something</label>
                  <div className="pe-ask">
                    <input id={`ps-change${uid}`} className="form-input" value={changeText} disabled={!ready || busyMaking} onChange={(e) => setChangeText(e.target.value)}
                      placeholder='e.g. "make the mould look more guilty", "Andrew in a cape"' />
                    <button type="submit" className="btn btn-outline btn-sm" disabled={!ready || busyMaking || !changeText.trim()}>Go</button>
                  </div>
                </form>

                <form className="ps-open-tool" onSubmit={(e) => { e.preventDefault(); addWords(); }}>
                  <label htmlFor={`ps-words${uid}`}>Words on it (spelt exactly as typed)</label>
                  <div className="pe-ask">
                    <input id={`ps-words${uid}`} className="form-input" maxLength={140} value={words.text} disabled={busy === 'words'} onChange={(e) => setWords((w) => ({ ...w, text: e.target.value }))}
                      placeholder="e.g. Spotted: mould on the move" />
                    <button type="submit" className="btn btn-outline btn-sm" disabled={busy === 'words' || !words.text.trim()}>{busy === 'words' ? '…' : 'Add'}</button>
                  </div>
                  <div className="ps-row ps-words-opts">
                    <label><input type="radio" name={`ps-where${uid}`} checked={words.where === 'bottom'} onChange={() => setWords((w) => ({ ...w, where: 'bottom' }))} /> Bottom</label>
                    <label><input type="radio" name={`ps-where${uid}`} checked={words.where === 'top'} onChange={() => setWords((w) => ({ ...w, where: 'top' }))} /> Top</label>
                    <label><input type="checkbox" checked={words.brand} onChange={(e) => setWords((w) => ({ ...w, brand: e.target.checked }))} /> “Get It Done” in the corner</label>
                  </div>
                </form>

                <div className="ps-open-delete">
                  {confirmDelete
                    ? <button type="button" className="btn btn-danger btn-sm" disabled={busy === 'delete'} onClick={remove}>Yes, delete it{open.used_on.length ? ' (posts keep their copy)' : ''}</button>
                    : <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirmDelete(true)}>Delete</button>}
                </div>
              </div>
            </div>
          )}

          {freshList.length > 0 && !freshList.every((p) => p.id === openId) && (
            <>
              <h3 className="ps-h">Just made</h3>
              <PictureGrid list={freshList} openId={openId} fresh={fresh} onOpen={openPicture} />
            </>
          )}

          <h3 className="ps-h">All pictures {pics ? <span className="text-muted">({pics.total})</span> : null}</h3>
          {!pics ? <div className="pl-empty"><div className="loading-spinner"></div></div>
            : list.length === 0 ? <p className="text-muted">None yet. Describe one on the left, or ask for ideas.</p>
              : <PictureGrid list={list} openId={openId} fresh={fresh} onOpen={openPicture} />}
        </div>
      </div>

      {castEditable && cast && (
        <details className="ps-cast" open={Boolean(castOpen)} onToggle={(e) => setCastOpen(e.currentTarget.open)}>
          <summary>Our cast <span className="text-muted">(the photos the AI draws you from)</span></summary>
          <p className="text-muted">Pick 1 to 4 clear photos of each of you: face visible, good light, ideally one close-up and one full-length. Only the first two go with each picture. The line under each is how you usually look on a job (it helps the AI dress you).</p>
          <div className="ps-cast-grid">
            {cast.map((c) => (
              <div key={c.key} className="ps-cast-card" data-cast={c.key}>
                <strong>{c.name}</strong>
                <div className="ps-cast-photos">
                  {c.library_ids.map((id) => (
                    <div key={id} className="ps-cast-photo">
                      <img src={contentService.libraryUrl(id, 'thumb')} alt={c.name} />
                      <button type="button" title="Take this photo off" onClick={() => castPhotos(c.key, c.library_ids.filter((x) => x !== id))}>✕</button>
                    </div>
                  ))}
                  {c.library_ids.length < 4 && <button type="button" className="ps-cast-add" onClick={() => setCastPick(c.key)}>+ Photos</button>}
                </div>
                <div className="pe-ask">
                  <input className="form-input" maxLength={300} value={looks[c.key] ?? ''} onChange={(e) => setLooks((l) => ({ ...l, [c.key]: e.target.value }))}
                    placeholder="e.g. navy Get It Done polo, cap, sunnies on the cap" />
                  <button type="button" className="btn btn-outline btn-sm" disabled={(looks[c.key] ?? '') === c.look} onClick={() => saveCast([{ key: c.key, look: looks[c.key] }])}>Save</button>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {castPick && (
        <Modal isOpen onClose={() => setCastPick(null)} title={`Photos of ${castName(castPick)}`} size="xl">
          <PhotoLibrary pickMode startKind="us" uploadKind="us" aiReady={aiReady}
            addLabel={(n) => `Use ${n === 1 ? 'this' : `these ${n}`} for ${castName(castPick)}`} onAdd={addCastPhotos} />
          <div className="modal-actions"><button type="button" className="btn btn-secondary" onClick={() => setCastPick(null)}>Close</button></div>
        </Modal>
      )}
      {lightbox && <Lightbox images={lightbox.images} startIndex={lightbox.index} onClose={() => setLightbox(null)} />}
    </div>
  );
}

function PictureGrid({ list, openId, fresh, onOpen }) {
  return (
    <div className="ps-grid-pics">
      {list.map((p) => (
        <button key={p.id} type="button" className={`ps-pic${p.id === openId ? ' on' : ''}`} onClick={() => onOpen(p.id)} data-picture={p.id} title={p.title || ''}>
          <img src={contentService.pictureUrl(p.id, 'thumb')} alt={p.alt || p.title || 'Picture'} loading="lazy" />
          <span className="ps-pic-badges">
            {fresh.includes(p.id) && <span className="ps-badge new">New</span>}
            {KIND_BADGE[p.kind] && <span className="ps-badge">{KIND_BADGE[p.kind]}</span>}
            {p.used_on.length > 0 && <span className="ps-badge used">On a post</span>}
          </span>
        </button>
      ))}
    </div>
  );
}
